import { v4 as uuid } from 'uuid';
import { useCallback, useEffect, useMemo } from 'react';
import PolarisActorProvider from 'src/components/PolarisActorProvider';
import withSnowplowTracking from 'src/hocs/withSnowplowTracking';
import MiniInterviewComponent from 'src/interviews';
import usePolaris from './hooks/usePolaris';

import 'src/index.css';

import type { ReactNode } from 'react';
import type {
  EventData,
  IInterviewConfig,
  IPolarisInterviewsProps,
} from 'src/packages/polaris/types';

type Props = IPolarisInterviewsProps & {
  title?: ReactNode;
};

const MiniInterview = withSnowplowTracking(MiniInterviewComponent);

function PolarisInterviews(props: Props) {
  const { configs, eventEmitter, interviewSettings, isOpen, setIsPopUpOpen } = props;
  const interviewToken = useMemo(() => uuid(), []);

  const { send, state } = usePolaris();

  const { currentInterview } = state?.context || {};

  /**
   * This callback opens and sets the selected interview (`id`) active. This can happen in
   * multiple ways, including answering the first question in an embedded interview module and
   * clicking on a Polaris link (polaris://interview?action=open&type=symptoms)
   */
  const handleOpenInterview = useCallback(
    (data?: EventData) => {
      if (data?.id) {
        // If there is a current interview and the user selects another interview, replace the
        // previous interview with the selected interview.
        if (currentInterview && currentInterview !== data.id) {
          send?.('TRANSITION', { id: data.id });
        } else {
          send?.('SUBMIT', { id: data?.id });
        }
      }
    },
    [currentInterview, send],
  );

  // This effect handles the user clicks a Polaris link in the host app, opening and selecting
  // an interview.
  useEffect(() => {
    if (eventEmitter) {
      eventEmitter.on('OPEN_INTERVIEW', handleOpenInterview);
    }

    return () => {
      eventEmitter?.off('OPEN_INTERVIEW', handleOpenInterview);
    };
  }, [eventEmitter, handleOpenInterview]);

  return (
    <>
      {configs.map((config: IInterviewConfig, i) => {
        const { id } = config;

        const onQuestionSubmit = (questionIndex: number) => {
          // This handles a user answering a question from an interview other than the
          // active interview.
          handleOpenInterview({ id });

          config.context?.onQuestionSubmit?.(questionIndex);
        };

        return (
          <PolarisActorProvider
            id={id}
            key={`${id}-${interviewToken}`}
            {...config.context}
            interviewToken={interviewToken}
            interviewType={config.interviewType}
            isInitialInterview={i === 0}
            parent={config.context.parent || 'polaris'}
            parentInterviewId={props.parentInterviewId}
            totalInterviews={configs.length}
            setIsPopUpOpen={setIsPopUpOpen}
            isOpen={isOpen}
            interviewSettings={interviewSettings}
          >
            <MiniInterview
              {...config.context}
              onQuestionSubmit={onQuestionSubmit}
              interviewType={config.interviewType}
              withBuoyTheme
            />
          </PolarisActorProvider>
        );
      })}
    </>
  );
}

export default PolarisInterviews;
